import appSettings from '../appsettings.json';
import AudioWave from "@/components/misc/AudioWave";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import {
	ActionIcon,
	Badge,
	Button,
	Container,
	Group, rem, Stack,
	Text,
	useMantineTheme
} from "@mantine/core";
import { IconHeart, IconHeartFilled } from "@tabler/icons-react";
import { OpenSheetMusicDisplay as OSMD } from "opensheetmusicdisplay";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

const SongDetails: React.FC = () => {
	const { songId } = useParams();
	const session = useContext(SessionContext);

	if (session === undefined) {
		throw new Error("SongDetails must be a child of SessionContext.");
	}

	const theme = useMantineTheme();

	const [song, setSong] = useState<Song | undefined>(undefined);
	const [songXML, setSongXML] = useState<string>("");
	const [songInLibrary, setSongInLibrary] = useState<boolean>(false);
	const [songInCart, setSongInCart] = useState<boolean>(false);
	const [songInWishlist, setSongInWishlist] = useState<boolean>(false);
	const [songTags, setSongTags] = useState<string[]>([]);

	const osmdContainerRef = useRef<HTMLDivElement | null>(null);
	const osmdInstance = useRef<OSMD | null>(null);

	useEffect(() => {
		if (session && session.marketplaceSongs) {
			setSong(session.marketplaceSongs.find(song => song.songId === songId))
		}
	}, [songId, session]);

	useEffect(() => {
		async function fetchSongXML() {
			if (songId && session) {
				const songXMLObj = await ConductionAPI.getSongMusicXml(songId);
				setSongXML(songXMLObj);
			}
		}
		fetchSongXML();
	}, [songId]);

	useEffect(() => {
		if (songXML && osmdContainerRef.current) {
			var fixedXML = songXML.trim();
			if (!songXML.startsWith('<?xml')) {
				fixedXML = '<?xml version="1.0" encoding="UTF-8"?>\n' + fixedXML;
			}
			osmdContainerRef.current.innerHTML = "";
			osmdInstance.current = new OSMD(osmdContainerRef.current, {
				backend: "svg",
				drawTitle: true,
				drawCredits: false,
				drawingParameters: "preview",
				autoResize: true,
				drawUpToMeasureNumber: 16,
				pageBackgroundColor: "",
			});
			osmdInstance.current
				.load(fixedXML)
				.then(() => osmdInstance.current?.render());
		}
	}, [songXML]);

	useEffect(() => {
		if (song) {
			setSongInLibrary(
				(session.userSongs ?? []).map((userSong) => userSong?.songId).includes(song.songId)
			);
		}
	}, [session.userSongs, song?.songId]);

	useEffect(() => {
		if (song) {
			setSongInCart(
				(session.songsInCart ?? []).map((cartSong) => cartSong?.songId).includes(song.songId)
			);
		}
	}, [session.songsInCart, song?.songId]);

	useEffect(() => {
		if (song) {
			setSongInWishlist(
				(session.songsInWishlist ?? []).map((wishlistSong) => wishlistSong?.songId).includes(song.songId)
			);
		}
	}, [session.songsInWishlist, song?.songId]);

	useEffect(() => {
		if (song && session.marketplaceSongs) {
			for (const s of session.marketplaceSongs) {
				if (s.songId === song?.songId) {
					setSongTags(s.tags || []);
				}
			}
		}
	}, [songTags, song?.songId]);

	const handleAddToCart = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			if (song) {
				if (songInCart) {
					session.setSongsInCart(
						(session.songsInCart ?? []).filter((cartSong) => cartSong.songId !== song.songId)
					);
				} else {
					session.setSongsInCart((session.songsInCart ?? []).concat(song));
				}
			}
		},
		[session.songsInCart, songInLibrary, songInCart, song]
	);

	const handleAddToWishlist = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			if (song) {
				if (songInWishlist) {
					session.setSongsInWishlist(
						(session.songsInWishlist ?? []).filter(
							(wishlistSong) => wishlistSong.songId !== song.songId
						)
					);
				} else {
					session.setSongsInWishlist((session.songsInWishlist ?? []).concat(song));
				}
			}
		},
		[session.songsInWishlist, songInWishlist, song]
	);


	return (
		<Stack
			gap={0}
		>
			{/* TopContainer: Sheet Music Viewer */}
			<Stack
				m={20}
				gap={0}
			>
				<div
					style={{
						width: "100%",
						height: "400px",
						overflow: "hidden",
						border: "2px solid black",
						borderRadius: rem(10),
						background: "white",
						position: "relative",
						cursor: "move",
					}}
				>
					<TransformWrapper
						minScale={0.5}
						maxScale={3}
						initialScale={1}
						wheel={{ disabled: false }}
						doubleClick={{ disabled: false }}
						pinch={{ disabled: false }}
						panning={{ disabled: false }}
						limitToBounds={false}
						centerOnInit
					>
						{({ zoomIn, zoomOut, resetTransform }) => (
							<>
								<Group
									style={{
										position: "relative",
										top: 8,
										right: -10,
										zIndex: 10,
										padding: ".5rem",
										rowGap: "100px",
									}}
								>
									<Button
										radius="xl"
										size="xs"
										style={{
											borderRadius: rem(100),
											background: "black",
											color: "white",
										}}
										onClick={() => zoomIn()}
									>
										+
									</Button>
									<Button
										radius="xl"
										size="xs"
										style={{
											borderRadius: rem(100),
											background: "black",
											color: "white",
										}}
										onClick={() => zoomOut()}
									>
										-
									</Button>
									<Button
										radius="xl"
										size="xs"
										style={{
											borderRadius: rem(100),
											background: "black",
											color: "white",
										}}
										onClick={() => resetTransform()}
									>
										Reset
									</Button>
								</Group>
								<TransformComponent>
									<div
										ref={osmdContainerRef}
										style={{
											minWidth: "100vw",
											minHeight: 300,
											width: "100%",
											height: "100%",
											background: "white",
										}}
									/>
								</TransformComponent>
							</>
						)}
					</TransformWrapper>
				</div>
			</Stack>
			{/* BottomContainer: Song Info */}
			<Stack
				gap={0}
				style={{
					flexDirection: "row",
					width: "100%",
					background: "#E8F1F9",
					border: "1px solid #ccc",
					height: "100%",
					justifyContent: "flex-start",
					flex: 1,
					minWidth: 0,
				}}
			>
				{/* LeftStack: Song Details and Buttons */}
				<Stack
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-start",
						padding: rem(24),
						overflowY: "auto",
						width: "50%",
						borderRight: `2px solid ${theme.colors.blue[7]}`,
						minWidth: 0,
					}}
				>
					<Stack>
						<Group style={{ alignItems: "baseline" }}>
							<Text mt="sm" style={{ fontSize: 50 }}>
								{song?.songName}
							</Text>
						</Group>
						{song?.artists.map((artist) => (
							<Text key={artist.artistId} mt="sm" fz="lg" style={{ display: "flex" }}>
								{artist.artistName}
							</Text>
						))}
					</Stack>
					<Group mt="xs" style={{ flex: 1, alignItems: "center" }}>
						{song?.price &&
							<Button
								radius="md"
								size="lg"
								style={{
									backgroundColor: songInCart ? theme.colors.blue[7] : undefined,
									color: songInCart ? theme.white : undefined,
								}}
								onClick={handleAddToCart}
							>
								{songInCart ? "Added To Cart" : "Add To Cart"}
							</Button>
						}
						{song?.price ? (
							<ActionIcon
								variant="default"
								radius="md"
								size={36}
								style={{ color: "#3A668C" }}
								onClick={handleAddToWishlist}
							>
								{songInWishlist ? (
									<IconHeartFilled style={{ color: "#3A668C" }} />
								) : (
									<IconHeart style={{ color: "#3A668C" }} />
								)}
							</ActionIcon>
						) : (
							<ActionIcon
								variant="default"
								radius="md"
								size={36}
								w={"20%"}
								style={{ color: "#3A668C" }}
								onClick={handleAddToWishlist}
							>
								{songInWishlist ? (
									<IconHeartFilled style={{ color: "#3A668C" }} />
								) : (
									<IconHeart style={{ color: "#3A668C" }} />
								)}
							</ActionIcon>
						)}
						{song?.price &&
							<Badge size="xl" variant="dark">
								{"$" + (song?.price?.toFixed(2) ?? "0.00")}
							</Badge>
						}
					</Group>
				</Stack>
				{/* RightStack: Tags and Audio */}
				<Stack
					style={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "auto",
						width: "50%",
						height: "100%",
						minWidth: 0,
					}}
				>
					<Group gap={7} mt={5} p={15} style={{ flexWrap: "wrap", }}>
						{(songTags ?? []).map((tag) => (
							<Badge variant="light" size="md" key={tag}>
								{tag}
							</Badge>
						))}
					</Group>
					<Container style={{ width: "100%" }}>
						<AudioWave url={`${appSettings.S3BucketBaseUrl}MarketplaceMP3s/${songId}.mp3`} />
					</Container>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default SongDetails;